import { Component, OnInit, effect, DestroyRef, inject, ChangeDetectionStrategy } from '@angular/core';
import { RouterOutlet, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MenubarModule } from 'primeng/menubar';
import { BadgeModule } from 'primeng/badge';
import { MenuItem } from 'primeng/api';
import { AuthService } from './services/auth.service';
import { UserMenuComponent } from './components/shared/user-menu/user-menu.component';
import { HelpdeskNotificationService } from './services/helpdesk-notification.service';

@Component({
  selector: 'app-root',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, RouterOutlet, MenubarModule, BadgeModule, UserMenuComponent],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss'
})
export class AppComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  title = 'Travvise-Tenant-Portal';
  menuItems: MenuItem[] = [];
  isAuthenticated = false;

  private helpdeskItem: MenuItem = {
    label: 'HelpDesk',
    icon: 'pi pi-headphones',
    command: () => this.router.navigate(['/helpdesk'])
  };

  constructor(
    private router: Router,
    private authService: AuthService,
    private helpdeskNotification: HelpdeskNotificationService
  ) {
    // Update badge whenever notification count changes
    effect(() => {
      const count = this.helpdeskNotification.newPnrCount();
      this.helpdeskItem.badge = count > 0 ? String(count) : undefined;
      this.helpdeskItem.badgeStyleClass = count > 0 ? 'p-badge-danger' : '';
      // Trigger change detection for menubar
      this.menuItems = [...this.menuItems];
    });

    this.menuItems = [
      {
        label: 'Dashboard',
        icon: 'pi pi-home',
        command: () => this.router.navigate(['/dashboard'])
      },
      {
        label: 'Tenant Onboarding',
        icon: 'pi pi-building',
        command: () => this.router.navigate(['/wizard'])
      },
      this.helpdeskItem,
      {
        label: 'Reports',
        icon: 'pi pi-chart-bar',
        command: () => this.router.navigate(['/reports'])
      },
      {
        label: 'Manage',
        icon: 'pi pi-cog',
        items: [
          {
            label: 'Supplier',
            icon: 'pi pi-building',
            command: () => this.router.navigate(['/manage/supplier'])
          },
          {
            label: 'Tenant Suppliers',
            icon: 'pi pi-link',
            command: () => this.router.navigate(['/manage/tenant-suppliers'])
          },
          {
            label: 'Business Plan',
            icon: 'pi pi-briefcase',
            command: () => this.router.navigate(['/manage/business-plan'])
          },
          {
            label: 'Account Status',
            icon: 'pi pi-check-circle',
            command: () => this.router.navigate(['/manage/account-status'])
          },
          {
            label: 'Sales Channels',
            icon: 'pi pi-shopping-cart',
            command: () => this.router.navigate(['/manage/sales-channels'])
          },
          {
            label: 'Smart Assist',
            icon: 'pi pi-list',
            command: () => this.router.navigate(['/manage/smart-assist'])
          },
          {
            label: 'SmartServe',
            icon: 'pi pi-envelope',
            command: () => this.router.navigate(['/manage/smartserve'])
          },
          {
            label: 'SmartReach',
            icon: 'pi pi-megaphone',
            command: () => this.router.navigate(['/manage/smartreach'])
          },
          {
            label: 'SmartEngage',
            icon: 'pi pi-whatsapp',
            command: () => this.router.navigate(['/manage/smartengage'])
          },
          {
            label: 'Hosting Type',
            icon: 'pi pi-server',
            command: () => this.router.navigate(['/manage/hosting-type'])
          },
          {
            label: 'Environments',
            icon: 'pi pi-globe',
            command: () => this.router.navigate(['/manage/environments'])
          },
          {
            label: 'Value Products',
            icon: 'pi pi-dollar',
            command: () => this.router.navigate(['/manage/value-products'])
          },
          {
            separator: true
          },
          {
            label: 'Roles',
            icon: 'pi pi-shield',
            command: () => this.router.navigate(['/manage/roles'])
          },
          {
            label: 'Permissions',
            icon: 'pi pi-key',
            command: () => this.router.navigate(['/manage/permissions'])
          },
          {
            separator: true
          },
          {
            label: 'Organizational Units',
            icon: 'pi pi-sitemap',
            command: () => this.router.navigate(['/manage/organizational-units'])
          },
          {
            separator: true
          },
          {
            label: 'Product Access',
            icon: 'pi pi-key',
            command: () => this.router.navigate(['/manage/value-product-access'])
          },
          {
            label: 'Billing',
            icon: 'pi pi-credit-card',
            command: () => this.router.navigate(['/manage/billing'])
          },
          {
            separator: true
          },
          {
            label: 'Tenants',
            icon: 'pi pi-building-columns',
            command: () => this.router.navigate(['/manage/tenants'])
          },
          {
            separator: true
          },
          {
            label: 'Migration Management',
            icon: 'pi pi-database',
            command: () => this.router.navigate(['/manage/migration-management'])
          }
        ]
      }
    ];
  }

  ngOnInit(): void {
    // Subscribe to auth state changes
    this.authService.authState$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(state => {
      this.isAuthenticated = state.IsAuthenticated;
    });
  }
}
